import React, {useState} from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import {Container} from "components/misc/Layouts";
import tw from "twin.macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";

import studyList from "../studyList.json";
import styled from "styled-components";
import {Helmet} from "react-helmet";

const ContentWithPaddingLg = tw.div`max-w-screen-xl mx-auto pb-4 lg:py-16`;
const HeadingRow = tw.div`flex flex-col md:flex-row md:items-center md:justify-between gap-4`;
const Heading = tw.h2`text-2xl md:text-4xl font-black tracking-wide text-gray-900`;
const LogoContainer = tw.div`flex items-center gap-4 justify-center md:justify-end`;
const Logo = tw.img`h-12 w-auto object-contain`;


const CollapsiblePanel = styled.div`
  ${tw`border border-gray-300 rounded-lg mb-4 overflow-hidden`}
`;

const PanelHeader = styled.button`
  ${tw`w-full px-6 py-4 text-left focus:outline-none transition-colors duration-200 flex justify-between items-center`}
  background-color: #c7942d;
  font-family: 'Poppins Medium';
  font-size: 18px;
  color: white;
  
  &:hover {
    opacity: 0.9;
  }
`;

const PanelContent = styled.div`
  ${tw`px-6 py-4 bg-white`}
  font-family: 'Poppins';
  font-size: 16px;
  color: #555;
  
  p {
    margin-bottom: 16px;
  }
  
  ul {
    margin: 16px 0;
    padding-left: 20px;
    list-style-type: disc;
  }
  
  li {
    margin-bottom: 8px;
  }
  
  h4 {
    font-family: 'Poppins Medium';
    font-size: 18px;
    margin: 24px 0 8px 0;
    color: #333;
  }
  
  a {
    color: #c7942d;
    text-decoration: underline;
  }
`;

const ChevronIcon = styled.span`
  ${tw`transform transition-transform duration-200`}
  ${props => props.isOpen ? 'rotate-180' : 'rotate-0'}
  color: white;
`;

const StudySection = styled.div`
  margin-bottom: 40px;
`;

const StudyTitle = styled.h2`
  font-family: 'Poppins Semibold';
  font-size: 24px;
  color: #333;
  margin-bottom: 20px;
`;

const StudyText = styled.p`
  font-family: 'Poppins';
  font-size: 16px;
  color: #555;
  line-height: 1.6;
  margin-bottom: 16px;
`;

const StudyTextBold = styled.p`
  font-family: 'Poppins Semibold';
  font-size: 16px;
  color: #333;
  line-height: 1.6;
  margin-bottom: 24px;
`;

const StudySubtitle = styled.h3`
  font-family: 'Poppins Semibold';
  font-size: 20px;
  color: #333;
  margin-bottom: 16px;
`;

const StudyDisclaimer = styled.p`
  font-family: 'Poppins';
  font-size: 14px;
  color: #666;
  font-style: italic;
  margin-bottom: 24px;
`;

const BoxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const BoxCard = styled.div`
  background-color: #f8f9fa;
  border: 2px solid #e9ecef;
  border-radius: 12px;
  padding: 24px;
  text-align: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 25px rgba(0,0,0,0.1);
  }
  
  h4 {
    font-family: 'Poppins Semibold';
    font-size: 18px;
    color: #333;
    margin-bottom: 12px;
  }
`;

// Hero Section Styles
const HeroSection = styled.div`
  ${tw`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start my-4 md:my-12`}
`;

const HeroContent = styled.div`
  ${tw`space-y-6`}
`;

const StudyBadge = styled.div`
  ${tw`inline-flex items-center gap-2 bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium mb-2 mt-4`}
  
  &::before {
    content: '●';
    font-size: 12px;
    color: #f59e0b;
  }
`;


const HeroDescription = styled.div`
  ${tw`text-lg space-y-4`}
  font-family: 'Poppins', sans-serif;
  
  p {
    margin-bottom: 1rem;
  }
  
  .highlight {
    font-family: "Poppins SemiBold";
    color: #1f2937;
  }
    
  .highlight-medium {
    font-family: "Poppins Medium";
    color: #1f2937;
  }
`;

const BenefitsGrid = styled.div`
  ${tw`grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8`}
`;

const BenefitCard = styled.div`
    ${tw`flex items-start gap-3 p-4 bg-gray-100 rounded-lg`}
    .icon {
        ${tw`flex-shrink-0 w-6 h-6 mt-1`}
        img {
            width: 24px;
            height: 24px;
        }
    }

    .content {
        font-family: 'Poppins', sans-serif;
        font-size: 15px;
        color: #374151;

        span {
            font-size: 17px;
        }
    }
`;

// Journey Timeline Styles
const JourneyCard = styled.div`
  ${tw`rounded-2xl p-6 lg:p-8 text-white relative overflow-hidden`}
  background-color: #051c48;
  background-image: url(${require("../images/study-journey.svg")});
  background-position: bottom right;
  background-repeat: no-repeat;
  background-size: auto;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 200px;
    height: 200px;
    background: linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%);
    border-radius: 50%;
    transform: translate(50%, -50%);
  }
`;

const JourneyHeader = styled.div`
  ${tw`flex items-center gap-3 mb-6`}
  
  .icon {
    ${tw`w-8 h-8`}
    
    img {
      width: 32px;
      height: 32px;
    }
  }
  
  h3 {
    ${tw`text-xl lg:text-2xl font-bold`}
    font-family: 'Poppins', sans-serif;
  }
`;

const TimelineContainer = styled.div`
  ${tw`relative`}
  
  &::before {
    content: '';
    position: absolute;
    left: 6px;
    top: 0;
    bottom: 0;
    width: 2px;
    background-color: #6b7280;
  }
`;

const TimelineItem = styled.div`
  ${tw`flex gap-4 relative pb-6`}
  
  &:last-child {
    padding-bottom: 0;
  }
  
  .marker {
    ${tw`flex-shrink-0 w-3 h-3 rounded-full mt-2 relative z-10`}
    background-color: ${props => props.active ? '#fbbf24' : '#6b7280'};
  }
  
  .content {
    ${tw`flex-1`}
    
    .week {
      ${tw`text-sm font-medium mb-1`}
        color: #ca9400;
    }
    
    .title {
      ${tw`text-lg font-semibold mb-2`}
    }
    
    .description {
      ${tw`text-blue-100 text-sm leading-relaxed`}
      font-family: 'Poppins', sans-serif;
    }
  }
`;


const CTAButton = styled.a`
  ${tw`inline-flex justify-center items-center gap-2 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold px-8 py-4 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl`}
  font-family: 'Poppins Medium', sans-serif;
  font-size: 18px;
  text-decoration: none;
  border-radius: 9999px;
  
  .arrow {
    ${tw`w-5`}
  }
`;

const DisclaimerText = styled.p`
    ${tw`text-sm text-gray-600 mt-4 italic`}
    font-family: 'Poppins', sans-serif;
    text-align: center;
`;

const StudyImage = styled.img`
  ${tw`w-full max-w-lg mx-auto rounded-lg shadow-lg my-6`}
`;

const CollapsibleSection = ({ title, children, defaultOpen = false }) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    return (
        <CollapsiblePanel>
            <PanelHeader onClick={() => setIsOpen(!isOpen)}>
                {title}
                <ChevronIcon isOpen={isOpen}>▼</ChevronIcon>
            </PanelHeader>
            {isOpen && (
                <PanelContent>
                    {children}
                </PanelContent>
            )}
        </CollapsiblePanel>
    );
};

export default () => {

    const post = studyList.find(a => a.slug === "achilles-tendon-treatment-study");

    if (!post) {
        return (
            <HeadingRow>
                <Heading>404 Study Paper not found</Heading>
            </HeadingRow>
        );
    }


    return (
        <>
            <Helmet>
                        <title>Achilles Tendon Pain Study - Free MRI & Non-Invasive Treatment | MAI Motion</title>
                        <meta name="description" content="Join our clinical study for Achilles tendinopathy. Get free MRI scans and test drug-free, non-compressive therapy. No surgery or injections required. Apply now." />
                        <meta name="keywords" content="achilles tendon pain, achilles tendinopathy study, clinical trial, free MRI scan, non-invasive treatment, drug-free therapy, tendon recovery, heel pain study, achilles injury research" />
                        <link rel="canonical" href="https://maimotion.com/study/achilles-tendon-treatment-study" />
                        <meta property="og:title" content="Achilles Tendon Pain Study - Free MRI & Non-Invasive Treatment" />
                        <meta property="og:description" content="Join our clinical study for Achilles tendinopathy. Get free MRI scans and test drug-free, non-compressive therapy. No surgery or injections required." />
                        <meta property="og:type" content="article" />
                        <meta property="og:url" content="https://maimotion.com/study/achilles-tendon-treatment-study" />
                        <meta property="og:image" content="https://d3ekn1do43nbxb.cloudfront.net/studies/images/achilles-tendon-treatment-study-3.jpg" />
                        <meta name="twitter:title" content="Achilles Tendon Pain Study - Free MRI & Non-Invasive Treatment" />
                        <meta name="twitter:description" content="Join our clinical study for Achilles tendinopathy. Get free MRI scans and test drug-free, non-compressive therapy." />
                        <meta name="twitter:card" content="summary_large_image" />
                        <meta name="twitter:image" content="https://d3ekn1do43nbxb.cloudfront.net/studies/images/achilles-tendon-treatment-study-3.jpg" />
                        <meta name="robots" content="index,follow" />
                        <meta name="author" content="MAI Motion Clinical Research Team" />
                        <meta name="geo.region" content="GB-LIN" />
                        <meta name="geo.placename" content="Lincolnshire, UK" />
                        <meta name="geo.position" content="53.0;-0.5" />
                        <meta name="ICBM" content="53.0, -0.5" />
                        <script type="application/ld+json">
                            {JSON.stringify({
                                "@context": "https://schema.org",
                                "@type": "MedicalStudy",
                                "name": "Achilles Tendon Treatment Clinical Study",
                                "description": "Clinical research study testing non-compressive, drug-free therapy for Achilles tendinopathy using semiconductor-embedded garments",
                                "sponsor": {
                                    "@type": "Organization",
                                    "name": "MAI Motion",
                                    "url": "https://maimotion.com"
                                },
                                "studyLocation": {
                                    "@type": "Place",
                                    "name": "Open MRI Centre Lincolnshire",
                                    "address": {
                                        "@type": "PostalAddress",
                                        "streetAddress": "London Rd, Silk Willoughby",
                                        "addressLocality": "Sleaford",
                                        "postalCode": "NG34 8NY",
                                        "addressCountry": "GB"
                                    }
                                },
                                "healthCondition": {
                                    "@type": "MedicalCondition",
                                    "name": "Achilles Tendinopathy",
                                    "alternateName": ["Achilles Tendon Pain", "Heel Pain"]
                                },
                                "eligibilityCriteria": "Adults with Achilles tendinopathy symptoms",
                                "url": "https://maimotion.com/study/achilles-tendon-treatment-study"
                            })}
                        </script>
            </Helmet>
            <AnimationRevealPage>
                <Header />
                <Container>
                    <ContentWithPaddingLg>

                        <HeadingRow>
                            <div>
                            <StudyBadge>CLINICAL RESEARCH STUDY</StudyBadge>
                            <Heading style={{ color: "#c7942d" }}>Are you suffering from Achillies Pain?</Heading>
                            </div>
                            <LogoContainer>
                                <Logo src={require("../images/incrediwear.jpeg")} alt="Incrediwear Logo" />
                                <Logo src={require("../images/msk-doctors.png")} alt="MSK Doctors Logo" />
                            </LogoContainer>
                        </HeadingRow>
                            <HeroSection>
                                <HeroContent>
                                    <HeroDescription>
                                        <p>
                                            We're running a new clinical study testing a <span className="highlight" style={{ fontSize: 20 }}>non-compressive, <span style={{ fontSize: 25, color: "#c7942d" }}>drug-free therapy</span></span> for Achilles tendinopathy — a common and stubborn injury.
                                        </p>
                                        <p className="highlight-medium">
                                            Help us shape the future of tendon care without surgery, injections, or medication.
                                        </p>
                                    </HeroDescription>
                                    <div style={{ marginTop: 30 }}>
                                        <CTAButton style={{ width: "100%", textAlign: "center" }} href="https://app.maimotion.com/eligibility-form/ACHILLES_STUDIES">
                                            Check If You're Eligible
                                            <span className="arrow">→</span>
                                        </CTAButton>
                                        <DisclaimerText>
                                            * Participation is voluntary. You can withdraw at any time.
                                        </DisclaimerText>
                                    </div>
                                    <div>
                                        <JourneyCard>
                                            <JourneyHeader>
                                                <div className="icon">
                                                    <img src={require("../images/calendar-clock.svg")} alt="Study Journey" />
                                                </div>
                                                <h3 style={{ fontFamily: "Poppins Semibold" }}>Study Journey (24 Weeks)</h3>
                                            </JourneyHeader>

                                            <TimelineContainer>
                                                <TimelineItem active>
                                                    <div className="marker"></div>
                                                    <div className="content">
                                                        <div className="week">WEEK 0</div>
                                                        <div className="title">Enrollment & 1st MRI</div>
                                                        <div className="description">
                                                            Initial assessment at our Lincolnshire Open MRI centre.
                                                        </div>
                                                    </div>
                                                </TimelineItem>

                                                <TimelineItem>
                                                    <div className="marker"></div>
                                                    <div className="content">
                                                        <div className="week" style={{ color: "#677590" }}>WEEKS 1 - 23</div>
                                                        <div className="title">Active Therapy</div>
                                                        <div className="description">
                                                            Daily wear of garments & brief weekly online check-ins.
                                                        </div>
                                                    </div>
                                                </TimelineItem>

                                                <TimelineItem active>
                                                    <div className="marker"></div>
                                                    <div className="content">
                                                        <div className="week">WEEK 24</div>
                                                        <div className="title">Final Review & 2nd MRI</div>
                                                        <div className="description">
                                                            Completion of the study and final progress imaging.
                                                        </div>
                                                    </div>
                                                </TimelineItem>
                                            </TimelineContainer>
                                        </JourneyCard>
                                    </div>
                                </HeroContent>
                                <BenefitsGrid>
                                    <BenefitCard>
                                        <div className="icon">
                                            <img src={require("../images/free-mri.svg")} alt="Free MRI" />
                                        </div>
                                        <div className="content">
                                            <span style={{ fontFamily: "Poppins Semibold" }}>Free MRI Scans</span><br />
                                            Two comprehensive Open MRI scans to monitor your tendon's progress.
                                        </div>
                                    </BenefitCard>

                                    <BenefitCard>
                                        <div className="icon">
                                            <img src={require("../images/exclusive-access.svg")} alt="Exclusive Access" />
                                        </div>
                                        <div className="content">
                                            <span style={{ fontFamily: "Poppins Semibold" }}>Exclusive Access</span><br />
                                            Access to advanced semiconductor garments during the study.
                                        </div>
                                    </BenefitCard>

                                    <BenefitCard>
                                        <div className="icon">
                                            <img src={require("../images/non-invasive.svg")} alt="Non-Invasive" />
                                        </div>
                                        <div className="content">
                                            <span style={{ fontFamily: "Poppins Semibold" }}>Non-Invasive</span><br />
                                            No surgery, no injections, and no medication involved.
                                        </div>
                                    </BenefitCard>

                                    <BenefitCard>
                                        <div className="icon">
                                            <img src={require("../images/support-science.svg")} alt="Support Science" />
                                        </div>
                                        <div className="content">
                                            <span style={{ fontFamily: "Poppins Semibold" }}>Support Science</span><br />
                                            Help evaluate regenerative treatments for active individuals.
                                        </div>
                                    </BenefitCard>
                                </BenefitsGrid>


                            </HeroSection>
                        <div className="mt-12">
                            <StudySection>
                                <StudyTitle>
                                    Help Shape the Future of Achilles Tendon Recovery
                                </StudyTitle>
                                <StudyText>
                                    Many people with Achilles tendinopathy struggle to find treatments that support recovery without restricting movement or relying on medication. Incrediwear garments are non-compressive and drug-free socks and ankle sleeves that incorporate therapeutic semiconductor materials within the fabric.
                                </StudyText>
                                <StudyText>
                                    When worn, the semiconductor elements are activated by the body's natural heat. This activation is intended to emit infrared waves, as well as negative ions, during normal daily activity. These signals influence circulation, inflammation, and pain-related pathways within the body.
                                </StudyText>
                                <StudyText>
                                    The purpose of this study is to evaluate how this wearable technology may support tendon recovery and symptom improvement in individuals with Achilles tendinopathy.
                                </StudyText>
                                <StudyText>
                                    Achilles pain can be frustrating, persistent, and limiting, especially if you enjoy running, sport, or simply staying active.
                                </StudyText>
                                <StudyTextBold>
                                    By taking part, you are not just testing a new approach, you are helping advance better, safer treatment options for people living with Achilles tendinopathy.
                                </StudyTextBold>

                                <StudySubtitle>
                                    Why take part?
                                </StudySubtitle>
                                <BoxGrid>
                                    <BoxCard>
                                        <h4>
                                            Contribute to innovative musculoskeletal research
                                        </h4>
                                    </BoxCard>
                                    <BoxCard>
                                        <h4>
                                            Gain detailed insight into your tendon health
                                        </h4>
                                    </BoxCard>
                                    <BoxCard>
                                        <h4>
                                            Support the development of future non-invasive treatments
                                        </h4>
                                    </BoxCard>
                                    <BoxCard>
                                        <h4>
                                            Access wearable intervention that fits into normal day routines
                                        </h4>
                                    </BoxCard>
                                </BoxGrid>
                                <StudyDisclaimer>
                                    *While individual benefit cannot be guaranteed, participation offers early access to a promising, low-risk approach under a formal research protocol.*
                                </StudyDisclaimer>

                                <StudyText>
                                    Participation is entirely voluntary, and all assessments are conducted under an approved research protocol.
                                </StudyText>

                                <div className="text-center mb-10">
                                    <CTAButton href="https://app.maimotion.com/eligibility-form/ACHILLES_STUDIES">
                                        Check If You're Eligible
                                        <span className="arrow">→</span>
                                    </CTAButton>
                                </div>
                            </StudySection>
                            <div className="mt-10">
                                <CollapsibleSection title="About the Study">
                                    <div>
                                        <h4>What Is This Study About?</h4>
                                        <p>Achilles tendinopathy affects the large tendon at the back of your ankle and can make walking, running, or even standing on tiptoe painful. It's often classified as:</p>
                                        <ul>
                                            <li>Insertional – where the tendon attaches to the heel</li>
                                            <li>Non-insertional – higher up in the tendon</li>
                                        </ul>
                                        <p>Common symptoms include heel pain, stiffness (especially in the morning), swelling, and discomfort during uphill walking or running.</p>

                                        <div className="flex flex-col lg:flex-row gap-6 mt-6">
                                            <div className="flex-1">
                                                <h4>How will I benefit from this study?</h4>
                                                <p>This study is testing a semiconductor-embedded garment system – a day ankle sleeve and a night therapy sock – designed to:</p>
                                                <ul>
                                                    <li>Boost blood and lymphatic flow</li>
                                                    <li>Reduce inflammation and cytokine activity</li>
                                                    <li>Support tendon recovery and muscle repair</li>
                                                    <li>Improve tendon vascularity and thickness (measured by MRI)</li>
                                                </ul>

                                                <h4>Why Is It Important?</h4>
                                                <p>Traditional treatments from exercises to surgery can be slow, invasive, or have mixed results. We are exploring whether this drug-free, non-invasive approach can speed up healing, improve function, and help people get back to doing what they love sooner.</p>

                                                <h4>What Will I Need to Do?</h4>
                                                <p>If you take part, you will:</p>
                                                <ul>
                                                    <li>Have a free MRI scan of your Achilles tendon at the start</li>
                                                    <li>Share information about your symptoms and activity levels</li>
                                                    <li>Wear the provided garments daily during the study period</li>
                                                </ul>
                                                <p className="font-medium mt-5">You won't need to take any medication or have injections!</p>
                                            </div>
                                            <div className="flex-shrink-0">
                                                <img
                                                    src="https://d3ekn1do43nbxb.cloudfront.net/studies/images/achilles-tendon-treatment-study-3.jpg"
                                                    alt="Achilles Tendinopathy Therapy"
                                                    style={{ maxWidth: '250px', borderRadius: '8px' }}
                                                />
                                            </div>
                                        </div>

                                        <a href="https://app.maimotion.com/eligibility-form/ACHILLES_STUDIES"
                                           style={{ display: 'inline-block', padding: '12px 24px', backgroundColor: '#c7942d', color: '#fff', fontFamily: 'Poppins Semibold', borderRadius: '8px', textDecoration: 'none', marginTop: '20px', marginBottom: '20px' }}>
                                            Check If You're Eligible
                                        </a>
                                    </div>
                                </CollapsibleSection>

                                <CollapsibleSection title="Location">
                                    <div>
                                        <h4>Open MRI Scan Location</h4>
                                        <h5 className="font-medium">London Rd, Silk Willoughby, Sleaford NG34 8NY, United Kingdom</h5>
                                        <h4 className="font-medium">Directions:</h4>
                                        <p>We are directly opposite to the Pennells - Four Season Garden centre on London Road (A153). There are plenty of free parking with very easy wheel chair access.</p>

                                        <div className="my-5">
                                            <iframe
                                                src="https://www.google.com/maps?q=Open+MRI+scan+%7C+Sleaford,+United+Kingdom&t=&z=17&ie=UTF8&iwloc=&output=embed"
                                                width="100%"
                                                height="300"
                                                style={{ border: '0', borderRadius: '8px' }}
                                                allowFullScreen=""
                                                loading="lazy"
                                                referrerPolicy="no-referrer-when-downgrade"
                                            ></iframe>
                                        </div>
                                        <a href="https://app.maimotion.com/eligibility-form/ACHILLES_STUDIES"
                                           style={{ display: 'inline-block', padding: '12px 24px', backgroundColor: '#c7942d', color: '#fff', fontFamily: 'Poppins Semibold', borderRadius: '8px', textDecoration: 'none', marginTop: '20px', marginBottom: '20px' }}>
                                            Check If You're Eligible
                                        </a>
                                    </div>
                                </CollapsibleSection>

                                <CollapsibleSection title="What Will Happen During the Study?">
                                    <div>
                                        <StudyImage
                                            src="https://d3ekn1do43nbxb.cloudfront.net/studies/images/achilles-tendon-treatment-study-5.jpeg"
                                            alt="Study Sponsors"
                                        />

                                        <p>If you choose to take part, you will follow a structured programme over 24 weeks. The study is designed to be safe, comfortable, and easy to follow, with clear milestones that help us monitor your progress.</p>
                                        <p>The aim is to evaluate how a non-compressive, drug-free infrared therapy garment system may support tendon healing, reduce symptoms, and improve day-to-day function.</p>

                                        <h4>Two MRI Scans – Open MRI in Lincolnshire</h4>
                                        <p>You will have two MRI scans as part of the study:</p>
                                        <ul>
                                            <li>At the start of the study</li>
                                            <li>At the 24-week follow-up</li>
                                        </ul>
                                        <p>Both scans will take place at our specialist research centre in Lincolnshire, using an Open MRI scanner.</p>
                                        <p>This type of scanner is more spacious, quiet, and comfortable than traditional tunnel-style MRI machines, making it suitable for individuals who prefer a less enclosed environment.</p>
                                        <p>These scans allow us to evaluate changes inside the Achilles tendon over time.</p>

                                        <StudyImage
                                            src="https://d3ekn1do43nbxb.cloudfront.net/studies/images/achilles-tendon-treatment-study-4.jpeg"
                                            alt="Open MRI Scanner"
                                        />

                                        <h4>Your Study Visit Schedule</h4>
                                        <p>The study includes a series of planned visits over 24 weeks.</p>

                                        <h4>Enrollment Visit (Week 0)</h4>
                                        <p>During your first visit, we will:</p>
                                        <ul>
                                            <li>Discuss the study and complete informed consent</li>
                                            <li>Perform your first MRI scan using our Open MRI</li>
                                            <li>Record your baseline symptoms and tendon function</li>
                                        </ul>
                                        <p>This visit gives us a clear starting point for your progress.</p>

                                        <h4>Follow-Up Visits</h4>
                                        <p>You will have follow-up assessments at:</p>
                                        <ul>
                                            <li>3 weeks</li>
                                            <li>6 weeks</li>
                                            <li>12 weeks</li>
                                            <li>24 weeks (Final Visit)</li>
                                        </ul>
                                        <p>At the 24-week final visit, you will also have your second MRI scan.</p>
                                        <p>This marks the end of your participation.</p>

                                        <h4>Weekly Home Check-Ins</h4>
                                        <p>You will also be required to complete brief weekly logs online. These take just a few minutes and help us monitor your day-to-day progress.</p>
                                        <p>Each week, you will record:</p>
                                        <ul>
                                            <li>Pain level (VAS)</li>
                                            <li>Garment/device usage</li>
                                            <li>Any medications taken (if applicable)</li>
                                        </ul>
                                        <p>These weekly updates help us understand how your symptoms change over time without requiring extra clinic visits.</p>
                                        <a href="https://app.maimotion.com/eligibility-form/ACHILLES_STUDIES"
                                           style={{ display: 'inline-block', padding: '12px 24px', backgroundColor: '#c7942d', color: '#fff', fontFamily: 'Poppins Semibold', borderRadius: '8px', textDecoration: 'none', marginTop: '20px', marginBottom: '20px' }}>
                                            Check If You're Eligible
                                        </a>
                                    </div>
                                </CollapsibleSection>

                                <div style={{ marginTop: '30px' }}>
                                    <h4 style={{ fontFamily: 'Poppins Medium', fontSize: '18px', marginBottom: '16px', color: '#333' }}>Your Commitment</h4>
                                    <p style={{ fontFamily: 'Poppins', fontSize: '16px', color: '#555', marginBottom: '16px' }}>During the study, you will:</p>
                                    <ul style={{ margin: '16px 0', paddingLeft: '20px', listStyleType: 'disc', fontFamily: 'Poppins', fontSize: '16px', color: '#555' }}>
                                        <li style={{ marginBottom: '8px' }}>Wear the provided semiconductor-embedded garments as instructed
                                            <ul style={{ marginTop: '8px', listStyleType: 'circle', marginLeft: '20px' }}>
                                                <li>Day garment: ankle sleeve</li>
                                                <li>Night garment: therapy sock</li>
                                            </ul>
                                        </li>
                                        <li style={{ marginBottom: '8px' }}>Complete short online questionnaires</li>
                                        <li style={{ marginBottom: '8px' }}>Attend scheduled follow-up visits</li>
                                        <li style={{ marginBottom: '8px' }}>Travel to Lincolnshire for two Open MRI scans</li>
                                    </ul>
                                    <p style={{ fontFamily: 'Poppins', fontSize: '16px', color: '#555', marginBottom: '16px' }}>There is no surgery, medication, or injections involved in the study.</p>

                                    <h4 style={{ fontFamily: 'Poppins Medium', fontSize: '18px', margin: '24px 0 8px 0', color: '#333' }}>Ethical Approval</h4>
                                    <p style={{ fontFamily: 'Poppins', fontSize: '16px', color: '#555', marginBottom: '16px' }}>This study has been reviewed and approved by a UK Research Ethics Committee and the Health Research Authority (HRA).</p>
                                    <p style={{ fontFamily: 'Poppins', fontSize: '16px', color: '#555', marginBottom: '16px' }}>This ensures the study meets all national standards for participant safety, data protection, and ethical conduct.</p>
                                    <p style={{ fontFamily: 'Poppins', fontSize: '16px', color: '#555', marginBottom: '16px' }}>You will receive full written information and have the opportunity to ask questions before deciding to take part.</p>

                                    <h4 style={{ fontFamily: 'Poppins Medium', fontSize: '18px', margin: '24px 0 8px 0', color: '#333' }}>Study Sponsors</h4>
                                    <p style={{ fontFamily: 'Poppins', fontSize: '16px', color: '#555', marginBottom: '16px' }}>This research is jointly sponsored by Incrediwear and MSK Doctors, supporting innovation in non-invasive tendon recovery and regenerative treatment technologies.</p>
                                    <a href="https://app.maimotion.com/eligibility-form/ACHILLES_STUDIES"
                                       style={{ display: 'inline-block', padding: '12px 24px', backgroundColor: '#c7942d', color: '#fff', fontFamily: 'Poppins Semibold', borderRadius: '8px', textDecoration: 'none', marginTop: '20px', marginBottom: '20px' }}>
                                        Check If You're Eligible
                                    </a>
                                </div>
                            </div>
                        </div>

                    </ContentWithPaddingLg>
                </Container>
                <Footer />
            </AnimationRevealPage>
        </>
    );
};
